/**
 * 折扣优惠相关交互（变体级别）
 *
 * 设计要点：
 * - custom.discount_money_count 定义在「变体」级别，因此每次变体切换都需要刷新
 *   price_second 块、折扣优惠块、底部固定栏折扣区、主图优惠角标的内容。
 * - 复用项目既有机制：ProductInfo.handleUpdateProductInfo() 会重新拉取整个分区 HTML，并通过
 *   publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId, html, variant } }) 广播。
 *   这里的自定义标签订阅该事件，从重新渲染的 html 中按自身 id 取出最新内容做替换 —— 这样金额格式化、
 *   折后价计算等全部交给服务端 Liquid，避免在 JS 里重复实现。
 * - 用户的折扣选择偏好由折扣块保留；只有当前变体存在折扣时，才在 <body> 上设置 discount-applied，
 *   视觉态由 CSS 驱动。
 */
(function () {
  'use strict';

  /**
   * 通用：变体切换时，用重新渲染的分区 HTML 替换自身内容（按 id 匹配）。
   * price_second 块、主图优惠角标都直接使用该标签。
   */
  class VariantRerender extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section || '';
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this._variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this._onVariantChange(event));
      }
      if (typeof this.afterRender === 'function') this.afterRender();
    }

    disconnectedCallback() {
      if (this._variantUnsub) this._variantUnsub();
    }

    _sectionMatches(data) {
      if (!data || data.sectionId == null) return true;
      if (!this.sectionId) return true;
      return data.sectionId.toString().includes(this.sectionId);
    }

    _onVariantChange(event) {
      const data = (event && event.data) || event;
      if (!this._sectionMatches(data) || !data || !data.html || !this.id) return;
      const fresh = data.html.getElementById(this.id);
      if (!fresh) return;
      this.innerHTML = fresh.innerHTML;
      if (typeof this.afterRender === 'function') this.afterRender();
    }
  }

  if (!customElements.get('variant-rerender')) {
    customElements.define('variant-rerender', VariantRerender);
  }

  /**
   * 折扣优惠块：点击区域切换 body.discount-applied。
   * 变体切换时复用 VariantRerender 的内容替换，并在替换后重新同步勾选框。
   */
  class DiscountToggle extends VariantRerender {
    connectedCallback() {
      // 「默认勾选」只用于初始化本块的用户偏好；当前变体没有折扣时不应让 body 保留折扣态。
      // 保留该偏好后，切回有折扣的变体时仍可恢复默认勾选效果。
      if (typeof this._requested === 'undefined') {
        this._requested = this.dataset.defaultChecked === 'true';
      }
      DiscountToggle._instances.add(this);
      super.connectedCallback();
      this._onClickHandler = this._onClick.bind(this);
      this.addEventListener('click', this._onClickHandler);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.removeEventListener('click', this._onClickHandler);
      DiscountToggle._instances.delete(this);
      DiscountToggle._syncAppliedState();
    }

    // 初次连接 & 每次变体重渲染后都会被调用：仅让有折扣的变体进入折扣态。
    afterRender() {
      this.checkbox = this.querySelector('[data-discount-checkbox]');
      DiscountToggle._syncAppliedState();
    }

    static _syncAppliedState() {
      let applied = false;
      DiscountToggle._instances.forEach((instance) => {
        const instanceApplied = Boolean(instance.checkbox) && instance._requested;
        if (instance.checkbox) instance.checkbox.checked = instanceApplied;
        if (instanceApplied) applied = true;
      });
      document.body.classList.toggle('discount-applied', applied);
    }

    _onClick() {
      if (!this.checkbox) return;
      this._requested = !this._requested;
      DiscountToggle._syncAppliedState();
    }
  }

  DiscountToggle._instances = new Set();

  if (!customElements.get('discount-toggle')) {
    customElements.define('discount-toggle', DiscountToggle);
  }
})();
